"use client";

import ActivitiesDropdown from "@/app/ActivitiesDropdown";
import type { DiscoverSelection } from "@/components/discover/filters/selection";
import LocationSelector from "@/components/location-selector/LocationSelector";
import { SearchButtonBase } from "@/components/SearchButtonBase";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useApplyPendingSearch } from "@/hooks/useApplyPendingSearch";
import pendingSearch from "@/lib/pendingSearch";
import { cn } from "@/lib/utils";
import type { Coords } from "@/types/places";
import { createTaxonomyFormatter } from "@/utils/formatTaxonomy";
import type { CustomLocation, MapAreaId } from "@/utils/location";
import { SearchIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useState } from "react";
import type { TranslationValues } from "use-intl/core";

type Translator = (key: string, values?: TranslationValues) => string;

export type SearchPreviewBarProps = {
  currentArea: MapAreaId;
  currentCustomLocation?: CustomLocation | null;
  coords: Coords;
  selection: DiscoverSelection;
  /**
   * If true, navigates to homepage after search (use on detail pages).
   * If false/undefined, updates current page URL (use on listing pages).
   */
  redirectToHome?: boolean;
};

const getActivityDisplayText = (
  activities: string[],
  format: ReturnType<typeof createTaxonomyFormatter>,
  t: Translator,
) => {
  if (activities.length === 1) {
    return format.activity(activities[0]);
  }

  if (activities.length > 1) {
    return t("search.multipleActivities", {
      count: activities.length,
    });
  }

  return t("search.selectActivity");
};

export function SearchPreviewBar({
  currentArea,
  currentCustomLocation,
  coords,
  selection,
  redirectToHome,
}: SearchPreviewBarProps) {
  const t = useTranslations();
  const format = createTaxonomyFormatter(t);
  const [open, setOpen] = useState(false);
  const [locationSelectorOpen, setLocationSelectorOpen] = useState(false);
  const { apply, isPending } = useApplyPendingSearch({
    redirectToHome,
    fallbackArea: currentArea,
  });

  const handleSearch = () => {
    const hasPendingChanges = pendingSearch.hasPendingChanges();

    // If no pending changes, don't apply search (keep dialog open to prompt selection)
    if (!hasPendingChanges) {
      return;
    }

    // Otherwise, apply the search and close the dialog
    apply();
    setOpen(false);
  };

  // Determine display text for activity
  const selectedActivities = selection.activities;
  const activityText = getActivityDisplayText(selectedActivities, format, t);

  // Determine display text for location
  const locationText = currentCustomLocation
    ? currentCustomLocation.displayName || t("locationSelector.yourLocation")
    : t(`mapAreas.${currentArea}.city`);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="w-full h-14 bg-accent rounded-full overflow-hidden flex items-center px-4 gap-3 cursor-pointer hover:bg-accent/80"
          aria-label={t("search.openSearch")}
        >
          {/* Activity icon */}
          <div className="relative w-8 h-8 rounded-md overflow-hidden shrink-0 bg-muted">
            <Image
              src="/search/search-activity.png"
              alt=""
              fill
              className="object-cover"
              sizes="32px"
            />
          </div>

          {/* Text content */}
          <div className="flex flex-col items-start justify-center flex-1 min-w-0">
            <span
              className={cn(
                "text-sm leading-5 truncate w-full text-left",
                selectedActivities.length > 0
                  ? "font-semibold text-foreground"
                  : "text-muted-foreground",
              )}
            >
              {activityText}
            </span>
            <span className="text-sm font-semibold text-foreground leading-5 truncate w-full text-left">
              {t("search.inLocation", {
                location: locationText,
              })}
            </span>
          </div>

          {/* Search button visual (not functional - whole bar is clickable) */}
          <div className="h-10 px-4 py-2 bg-spond-red rounded-full flex items-center gap-2 shadow-xs shrink-0">
            <SearchIcon className="w-5 h-5 text-white" />
            <span className="text-base font-medium text-white">
              {t("search.button")}
            </span>
          </div>
        </button>
      </DialogTrigger>

      {open && (
        <DialogContent
          className="w-[343px] h-[338px] p-4 gap-0 rounded-lg shadow-lg [&>button]:hidden top-[25%]"
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Title */}
          <DialogTitle className="text-lg font-semibold text-foreground text-center pb-6">
            {t("search.discover")}
          </DialogTitle>

          {/* Form content */}
          <div className="space-y-4">
            {/* Activity selector - "What" */}
            <div className="space-y-2">
              <label
                htmlFor="activity-selector"
                className="text-base font-medium text-foreground leading-6"
              >
                {t("search.what")}
              </label>
              <div className="h-12 border border-input rounded-full shadow-xs bg-white overflow-hidden [&_button]:h-full [&_button]:w-full [&_button]:border-0 [&_button]:shadow-none [&_button]:rounded-full">
                <ActivitiesDropdown coords={coords} selection={selection} />
              </div>
            </div>

            {/* Location selector - "Where" */}
            <div className="space-y-2">
              <label
                htmlFor="location-selector"
                className="text-base font-medium text-foreground leading-6"
              >
                {t("search.where")}
              </label>
              <div className="h-12 border border-input rounded-full shadow-xs bg-white overflow-hidden [&_button]:h-full [&_button]:w-full [&_button]:border-0 [&_button]:shadow-none [&_button]:rounded-full">
                <LocationSelector
                  currentArea={currentArea}
                  currentCustomLocation={currentCustomLocation}
                  isOpen={locationSelectorOpen}
                  onOpenChange={setLocationSelectorOpen}
                />
              </div>
            </div>
          </div>

          {/* Search button */}
          <div className="pt-6">
            <SearchButtonBase
              className="w-full h-12 rounded-full shadow-xs"
              onSearch={handleSearch}
              isLoading={isPending}
              alwaysExpanded
            />
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}
